import React, { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Trash2, CheckCircle, Upload } from 'lucide-react';
import { purchaseApi } from '../api/purchase';
import { coaApi } from '../api/coa';
import ProductPicker from '../components/ProductPicker';
import ContactPicker from '../components/ContactPicker';
import { useAuth } from '../hooks/useAuth';
import api from '../api/client';
import { useToast } from '../hooks/useToast';

interface Props { grnId: number; onBack: () => void; onConfirmed: () => void; }
const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', purple:'badge-purple', red:'badge-red' };

const GrnDetail: React.FC<Props> = ({ grnId, onBack, onConfirmed }) => {
  const { showToast } = useToast();
  const [grn, setGrn]                   = useState<any>(null);
  const [accounts, setAccounts]         = useState<any[]>([]);
  const [lineLandedAcct, setLineLandedAcct] = useState('');
  const [llSplits, setLlSplits] = useState<any[]>([{ account_id:'', amount:'' }]);
  const [loading, setLoading]           = useState(true);
  const [showLCForm, setShowLCForm]     = useState(false);
  const [showImport, setShowImport]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [importRows, setImportRows]     = useState<any[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [lcForm, setLcForm] = useState({ cost_type:'Transport', description:'', amount:'', credit_account_id:'', allocation_method:'by_value' });
  const [lcSplits, setLcSplits] = useState<any[]>([{ account_id:'', amount:'' }]);
  const [lcRows, setLcRows] = useState<any[]>([{ cost_type:'Transport', amount:'', credit_account_id:'', allocation_method:'by_value', description:'' }]);
  const { hasPermission } = useAuth();
  const canEditGrn = hasPermission('purchase.edit_grn');
  const canVoidGrn = hasPermission('purchase.void_grn');
  const [showCorrect, setShowCorrect] = useState(false);
  const [cMode, setCMode] = useState<'menu'|'edit'|'void'>('menu');
  const [eItems, setEItems] = useState<any[]>([]);
  const [eLanded, setELanded] = useState<any[]>([]);
  const [eDate, setEDate] = useState('');
  const [vReason, setVReason] = useState('');
  const [vDate, setVDate] = useState('');
  const [cMsg, setCMsg] = useState('');
  const [locations, setLocations] = useState<any[]>([]);
  const [eContact, setEContact] = useState('');
  const [eBranch, setEBranch] = useState('');
  const [branches, setBranches] = useState<any[]>([]);

  const openCorrect = () => {
    setCMode('menu'); setCMsg('');
    setEDate(grn.grn_date || ''); setVDate(grn.grn_date || ''); setVReason('');
    setEItems((grn.items || []).map((i:any) => ({
      id: i.id, product_id: i.product_id, product_name: i.product_name, location_id: i.location_id,
      qty_received: String(i.qty_received), unit_cost: String(i.base_unit_cost || i.unit_cost), landed_per_unit: String(Number(i.landed_per_unit || 0) || ''),
      batch_no: i.batch_no || '', expiry_date: i.expiry_date || '', qty_returned: Number(i.qty_returned || 0),
    })));
    setELanded((grn.landed_costs || []).map((l:any) => ({
      cost_type: l.cost_type, amount: String(l.amount), credit_account_id: String(l.credit_account_id),
      allocation_method: l.allocation_method || 'by_value',
    })));
    setEContact(grn.contact_id ? String(grn.contact_id) : '');
    setEBranch(grn.branch_id ? String(grn.branch_id) : '');
    api.get('/settings/locations').then(r => setLocations(r.data.data || [])).catch(() => {});
    api.get('/settings/branches').then(r => setBranches(r.data.data || [])).catch(() => {});
    setShowCorrect(true);
  };

  const addEItem = () => setEItems(l => [...l, { product_id:'', product_name:'', location_id:'', qty_received:'1', unit_cost:'0', landed_per_unit:'', batch_no:'', expiry_date:'', qty_returned:0 }]);
  const rmEItem = (i:number) => setEItems(l => l.filter((_,x) => x !== i));
  const addELanded = () => setELanded(l => [...l, { cost_type:'Transport', amount:'', credit_account_id:'', allocation_method:'by_value' }]);
  const rmELanded = (i:number) => setELanded(l => l.filter((_,x) => x !== i));

  const saveGrnEdit = async () => {
    setSaving(true); setCMsg('');
    try {
      const payload:any = { line_landed_splits: llSplits.filter((s:any) => s.account_id).map((s:any) => ({ account_id: Number(s.account_id), amount: llSplits.length === 1 ? null : Number(s.amount) })),
        grn_date: eDate || undefined,
        contact_id: eContact ? Number(eContact) : undefined,
        branch_id: eBranch ? Number(eBranch) : undefined,
        items: eItems.map(i => ({
          id: i.id || undefined, product_id: Number(i.product_id), location_id: Number(i.location_id),
          qty_received: Number(i.qty_received), unit_cost: Number(i.unit_cost), landed_per_unit: Number(i.landed_per_unit || 0),
          batch_no: i.batch_no || null, expiry_date: i.expiry_date || null,
        })),
      };
      const lc = eLanded.filter(l => l.credit_account_id && Number(l.amount) > 0);
      if (lc.length) payload.landed_costs = lc.map(l => ({ cost_type:l.cost_type, amount:Number(l.amount), credit_account_id:Number(l.credit_account_id), allocation_method:l.allocation_method }));
      const res = await api.put(`/grns/${grnId}`, payload);
      showToast(res.data.message || 'GRN updated');
      setShowCorrect(false); load();
    } catch (e:any) { setCMsg(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const doGrnVoid = async () => {
    setSaving(true); setCMsg('');
    try {
      const res = await api.post(`/grns/${grnId}/void`, { reason: vReason || undefined, reversal_date: vDate || undefined });
      showToast(res.data.message || 'GRN voided');
      setShowCorrect(false); load();
    } catch (e:any) { setCMsg(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const load = () => {
    setLoading(true);
    coaApi.list().then(r => setAccounts(r.data.data.filter((a: any) => a.type_name === 'Liability' || a.type_name === 'Expense' || (a.type_name === 'Asset' && !['1100','1200','1300'].includes(String(a.account_code)) && !/^1[5-9]/.test(String(a.account_code))))));
    fetch(`https://merchanerp.com/api/grns/${grnId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
    }).then(r => r.json()).then(d => { setGrn(d.data); setLoading(false); });
  };

  useEffect(() => { load(); }, [grnId]);

  const downloadTemplate = () => {
    const csv = [
      'sku,product_name,category,unit_symbol,branch_name,location_name,qty,unit_cost,selling_price,batch_no,expiry_date',
      'sa89,Samsung A13,Phones,pcs,Main Branch,Main Warehouse,100,25.50,30.00,,',
      'PROD-002,iPhone 14,Phones,pcs,Main Branch,Main Warehouse,50,800.00,950.00,BATCH-001,2027-12-31'
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'grn_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const res = await purchaseApi.importPreview(file);
      setImportRows(res.data.data.rows);
      setImportErrors(res.data.data.errors);
      showToast(`${res.data.data.total} rows ready.${res.data.data.errors.length > 0 ? ' '+res.data.data.errors.length+' warnings.' : ''}`, res.data.data.errors.length > 0 ? 'warning' : 'success');
    } catch (err: any) { showToast(err.response?.data?.message || 'Upload failed', 'error'); }
    finally { setImportLoading(false); }
  };

  const handleImportSave = async () => {
    if (importRows.length === 0) { showToast('No valid rows to import', 'error'); return; }
    const validRows = importRows.filter((r: any) => r.location_id);
    if (validRows.length === 0) { showToast('No rows with valid locations', 'error'); return; }
    setSaving(true);
    try {
      const res = await purchaseApi.importSave(grnId, { rows: validRows });
      showToast(res.data.message);
      setShowImport(false);
      setImportRows([]);
      setImportErrors([]);
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!grn) return;
    try {
      const s = grn.line_landed_splits ? JSON.parse(grn.line_landed_splits) : [];
      if (s.length) setLlSplits(s.map((x:any) => ({ account_id: String(x.account_id), amount: x.amount == null ? '' : String(x.amount) })));
      else if (grn.line_landed_account_id) setLlSplits([{ account_id: String(grn.line_landed_account_id), amount: '' }]);
    } catch { }
  }, [grn]); // eslint-disable-line

  const handleAddLC = async () => {
    const rows = lcRows.filter((x:any) => Number(x.amount) > 0);
    if (!rows.length || rows.some((x:any) => !x.credit_account_id)) { showToast('Each cost needs an amount and a paid-from account', 'error'); return; }
    setSaving(true);
    try {
      for (const x of rows) await purchaseApi.addLandedCost(grnId, { ...x, amount: Number(x.amount), credit_account_id: Number(x.credit_account_id) });
      showToast('Landed cost added!');
      setShowLCForm(false);
      setLcForm({ cost_type:'Transport', description:'', amount:'', credit_account_id:'', allocation_method:'by_value' }); setLcRows([{ cost_type:'Transport', amount:'', credit_account_id:'', allocation_method:'by_value', description:'' }]);
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteLC = async (costId: number) => {
    try { await purchaseApi.deleteLandedCost(grnId, costId); showToast('Removed'); load(); }
    catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleConfirm = async () => {
    if (!window.confirm('Confirm this GRN? This will post to accounting and update inventory.')) return;
    try {
      const res = await purchaseApi.confirmGrn(grnId, { line_landed_splits: llSplits.filter((s:any) => s.account_id).map((s:any) => ({ account_id: Number(s.account_id), amount: llSplits.length === 1 ? null : Number(s.amount) })) });
      showToast(res.data.message); onConfirmed();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;
  if (!grn) return <div className="empty-state"><p>GRN not found.</p></div>;

  const isDraft     = grn.status === 'Draft';
  const hasItems    = grn.items?.length > 0;
  const itemsTotal  = grn.items?.reduce((s: number, i: any) => s + (i.qty_received * i.unit_cost), 0) || 0;
  const landedTotal = grn.landed_costs?.reduce((s: number, l: any) => s + Number(l.amount), 0) || 0;

  return (
    <>
      {showCorrect && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div className="card" style={{width: cMode==='edit' ? 900 : 460, maxHeight:'88vh', overflowY:'auto'}}>
            <div className="card-header">
              <span>Correct {grn.grn_no}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCorrect(false)}>Close</button>
            </div>
            <div className="card-body">
              {cMsg && <div style={{marginBottom:12,padding:8,background:'var(--sur2)',borderRadius:4,fontSize:12,color:'var(--red)'}}>{cMsg}</div>}

              {cMode === 'menu' && (
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {canEditGrn && <button className="btn btn-secondary" style={{textAlign:'left',padding:'12px 14px'}} onClick={() => setCMode('edit')}>
                    <div style={{fontWeight:600}}>Edit this GRN</div>
                    <div style={{fontSize:11,color:'var(--tx3)',marginTop:2}}>Change items, costs, landed costs or date. Keeps the same document number.</div>
                  </button>}
                  {canVoidGrn && <button className="btn btn-secondary" style={{textAlign:'left',padding:'12px 14px'}} onClick={() => setCMode('void')}>
                    <div style={{fontWeight:600}}>Void this GRN</div>
                    <div style={{fontSize:11,color:'var(--tx3)',marginTop:2}}>Cancel it entirely. Stock and accounting are reversed.</div>
                  </button>}
                </div>
              )}

              {cMode === 'edit' && (
                <div>
                  <div style={{display:'grid',gridTemplateColumns:'200px 1fr 1fr',gap:10}}>
                    <div className="form-group">
                      <label className="form-label">GRN Date</label>
                      <input type="date" className="form-control" value={eDate} max={new Date().toISOString().split('T')[0]} onChange={e => setEDate(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Supplier</label>
                      <ContactPicker value={eContact} initialLabel={grn.supplier_name} placeholder="Type to search supplier" onSelect={(c:any) => setEContact(String(c.id))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Branch</label>
                      <select className="form-control" value={eBranch} onChange={e => setEBranch(e.target.value)}>
                        <option value="">Select branch</option>
                        {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{fontSize:12,fontWeight:600,margin:'12px 0 6px'}}>Items</div>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr style={{background:'var(--sur2)'}}>
                      <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product / Location</th>
                      <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Qty</th>
                      <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Base Cost</th>
                      <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Landed / unit</th>
                      <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Batch</th>
                      <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Expiry</th>
                      <th style={{width:30}}></th>
                    </tr></thead>
                    <tbody>
                      {eItems.map((it:any, idx:number) => (
                        <tr key={idx} style={{borderBottom:'1px solid var(--bdr)'}}>
                          <td style={{padding:'6px 6px',minWidth:220}}>
                            <ProductPicker value={it.product_id} initialLabel={it.product_name} locationId={it.location_id} fontSize={12}
                              onSelect={(p:any) => setEItems(l => l.map((x,i) => i===idx ? {...x, product_id:String(p.id), product_name:p.name} : x))} />
                            <select className="form-control" style={{marginTop:4,fontSize:11}} value={it.location_id}
                              onChange={e => setEItems(l => l.map((x,i) => i===idx ? {...x, location_id:e.target.value} : x))}>
                              <option value="">Select location</option>
                              {locations.map((l:any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                          </td>
                          <td style={{padding:'6px 4px',width:90,verticalAlign:'top'}}>
                            <input className="form-control mono" style={{textAlign:'right'}} value={it.qty_received}
                              onChange={e => setEItems(l => l.map((x,i) => i===idx ? {...x, qty_received:e.target.value} : x))} />
                            {it.qty_returned > 0 && <div style={{fontSize:10,color:'var(--tx3)',marginTop:2}}>Returned: {it.qty_returned}</div>}
                          </td>
                          <td style={{padding:'6px 4px',width:100,verticalAlign:'top'}}>
                            <input className="form-control mono" style={{textAlign:'right'}} value={it.unit_cost}
                              onChange={e => setEItems(l => l.map((x,i) => i===idx ? {...x, unit_cost:e.target.value} : x))} />
                          </td>
                      <td style={{ padding:'6px 4px', width:90 }}><input className="form-control mono" type="number" placeholder="0" value={it.landed_per_unit} onChange={e => setEItems(l => l.map((x,i) => i===idx ? {...x, landed_per_unit:e.target.value} : x))} style={{ textAlign:'right' }} /></td>
                          <td style={{padding:'6px 4px',width:110,verticalAlign:'top'}}>
                            <input className="form-control" style={{fontSize:11}} value={it.batch_no}
                              onChange={e => setEItems(l => l.map((x,i) => i===idx ? {...x, batch_no:e.target.value} : x))} />
                          </td>
                          <td style={{padding:'6px 4px',width:130,verticalAlign:'top'}}>
                            <input type="date" className="form-control" style={{fontSize:11}} value={it.expiry_date ? String(it.expiry_date).split('T')[0] : ''}
                              onChange={e => setEItems(l => l.map((x,i) => i===idx ? {...x, expiry_date:e.target.value} : x))} />
                          </td>
                          <td style={{padding:'6px 4px',textAlign:'center',verticalAlign:'top'}}>
                            {eItems.length > 1 && <button className="btn btn-secondary btn-sm" style={{padding:'2px 6px'}} onClick={() => rmEItem(idx)}>x</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button className="btn btn-secondary btn-sm" style={{marginTop:8}} onClick={addEItem}>+ Add Item</button>
                {(() => {
                  const t = eItems.reduce((s:number,x:any) => s + Number(x.qty_received||0) * Number(x.landed_per_unit||0), 0);
                  if (t <= 0) return null;
                  const sp = llSplits.reduce((s:number,x:any) => s + Number(x.amount||0), 0);
                  return (
                    <div style={{marginTop:10,fontSize:12}}>
                      <div style={{marginBottom:6}}>Per-unit landed costs: <strong style={{fontFamily:'monospace'}}>{t.toFixed(2)}</strong> — paid from:</div>
                      {llSplits.map((s:any, i:number) => (
                        <div key={i} style={{display:'flex',gap:6,marginBottom:4,maxWidth:560}}>
                          <select className="form-control" style={{flex:1}} value={s.account_id} onChange={e => setLlSplits(x => x.map((y:any,j:number) => j===i ? {...y, account_id:e.target.value} : y))}><option value="">Select account</option>{accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select>
                          {llSplits.length > 1 && <input className="form-control mono" style={{width:110,textAlign:'right'}} placeholder="Amount" value={s.amount} onChange={e => setLlSplits(x => x.map((y:any,j:number) => j===i ? {...y, amount:e.target.value} : y))} />}
                          {llSplits.length > 1 && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLlSplits(x => x.filter((_:any,j:number) => j!==i))}>x</button>}
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLlSplits(x => [...x, { account_id:'', amount:'' }])}>+ Split account</button>
                      {llSplits.length > 1 && <span style={{marginLeft:10,color: Math.abs(sp - t) > 0.005 ? 'var(--red)' : 'var(--tx3)'}}>Splits {sp.toFixed(2)} of {t.toFixed(2)}</span>}
                    </div>
                  );
                })()}

                  <div style={{fontSize:12,fontWeight:600,margin:'16px 0 6px',paddingTop:12,borderTop:'1px solid var(--bdr)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span>Landed Costs</span>
                    <button className="btn btn-secondary btn-sm" onClick={addELanded}>+ Add</button>
                  </div>
                  {eLanded.length === 0 && <div style={{fontSize:11,color:'var(--tx3)'}}>No landed costs.</div>}
                  {eLanded.map((lc:any, li:number) => (
                    <div key={li} style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
                      <input className="form-control" style={{width:130,fontSize:12}} placeholder="Type" value={lc.cost_type}
                        onChange={e => setELanded(l => l.map((x,i) => i===li ? {...x, cost_type:e.target.value} : x))} />
                      <input className="form-control mono" style={{width:110,textAlign:'right',fontSize:12}} placeholder="Amount" value={lc.amount}
                        onChange={e => setELanded(l => l.map((x,i) => i===li ? {...x, amount:e.target.value} : x))} />
                      <select className="form-control" style={{flex:1,fontSize:12}} value={lc.credit_account_id}
                        onChange={e => setELanded(l => l.map((x,i) => i===li ? {...x, credit_account_id:e.target.value} : x))}>
                        <option value="">Credit account</option>
                        {accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
                      </select>
                      <select className="form-control" style={{width:120,fontSize:12}} value={lc.allocation_method}
                        onChange={e => setELanded(l => l.map((x,i) => i===li ? {...x, allocation_method:e.target.value} : x))}>
                        <option value="by_value">By value</option>
                        <option value="by_qty">By qty</option>
                      </select>
                      <button className="btn btn-secondary btn-sm" style={{padding:'2px 6px'}} onClick={() => rmELanded(li)}>x</button>
                    </div>
                  ))}

                  <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}>
                    <button className="btn btn-secondary" onClick={() => setCMode('menu')}>Back</button>
                    <button className="btn btn-primary" onClick={saveGrnEdit} disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button>
                  </div>
                </div>
              )}

              {cMode === 'void' && (
                <div>
                  <div style={{marginBottom:14,fontSize:13}}>This will reverse the accounting and remove the received stock. {grn.grn_no} will be marked Cancelled.</div>
                  <div className="form-group">
                    <label className="form-label">Reversal date</label>
                    <input type="date" className="form-control" value={vDate} max={new Date().toISOString().split('T')[0]} onChange={e => setVDate(e.target.value)} style={{width:180}} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason</label>
                    <input className="form-control" value={vReason} onChange={e => setVReason(e.target.value)} placeholder="Optional" />
                  </div>
                  <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}>
                    <button className="btn btn-secondary" onClick={() => setCMode('menu')}>Back</button>
                    <button className="btn btn-primary" style={{background:'var(--red)',borderColor:'var(--red)'}} onClick={doGrnVoid} disabled={saving}>{saving ? 'Voiding...' : 'Void GRN'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-secondary btn-sm" onClick={onBack}><ArrowLeft size={13} /> Back</button>
          <h1 className="page-title">{grn.grn_no}</h1>
          <span className={`badge ${colorMap[grn.status_color]||'badge-gray'}`}>{grn.status}</span>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {isDraft && !hasItems && <button className="btn btn-secondary" onClick={() => { setShowImport(!showImport); setShowLCForm(false); }}><Upload size={13} /> Import CSV</button>}
          {isDraft && hasItems && <button className="btn btn-secondary" onClick={() => { setShowLCForm(!showLCForm); setShowImport(false); }}><Plus size={13} /> Landed Cost</button>}
          {isDraft && hasItems && <button className="btn btn-success" onClick={handleConfirm}><CheckCircle size={13} /> Confirm GRN</button>}
          {grn.status === 'Received' && (canEditGrn || canVoidGrn) && <button className="btn btn-secondary" onClick={openCorrect}>Correct</button>}
        </div>
      </div>

      <div className="form-grid-3" style={{ marginBottom:16 }}>
        <div className="kpi-card"><div className="kpi-label">Supplier</div><div style={{ fontSize:14, fontWeight:600 }}>{grn.supplier_name}</div></div>
        <div className="kpi-card"><div className="kpi-label">Items Total</div><div className="kpi-value" style={{ fontSize:18, color:'var(--blue)' }}>{itemsTotal.toFixed(2)}</div></div>
        <div className="kpi-card"><div className="kpi-label">Landed Costs</div><div className="kpi-value" style={{ fontSize:18, color:'var(--amber)' }}>{landedTotal.toFixed(2)}</div></div>
      </div>

      {showImport && isDraft && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header">
            <span>Import Items from CSV</span>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowImport(false); setImportRows([]); setImportErrors([]); }}>Cancel</button>
          </div>
          <div className="card-body">
            <div className="alert alert-info" style={{ marginBottom:12 }}>
              <strong>Required:</strong> sku, qty, unit_cost, product_name, category, unit_symbol, branch_name, location_name &nbsp;
              <strong>Optional:</strong> selling_price, batch_no, expiry_date
              <button className="btn btn-secondary btn-sm" style={{ marginLeft:12 }} onClick={downloadTemplate}>Download Template</button>
            </div>
            <div className="form-group" style={{ marginBottom:14 }}>
              <label className="form-label">Upload CSV File</label>
              <input type="file" accept=".csv,.txt" className="form-control" onChange={handleFileUpload} disabled={importLoading} />
            </div>
            {importErrors.length > 0 && (
              <div className="alert alert-warning" style={{ marginBottom:12 }}>
                <strong>{importErrors.length} warnings:</strong>
                <ul style={{ margin:'8px 0 0 16px', fontSize:11 }}>
                  {importErrors.map((e,i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            {importRows.length > 0 && (
              <>
                <div style={{ marginBottom:8, fontSize:12, fontWeight:600, color:'var(--tx2)' }}>
                  Preview — {importRows.length} rows — Total: {importRows.reduce((s,r) => s + r.line_total, 0).toFixed(2)}
                </div>
                <div style={{ maxHeight:300, overflowY:'auto', border:'1px solid var(--bdr)', borderRadius:7, marginBottom:14 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'var(--sur2)', position:'sticky', top:0 }}>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>SKU</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Product</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase' }}>Location</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Cost</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Sell Price</th>
                      <th style={{ padding:'8px 12px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
                    </tr></thead>
                    <tbody>
                      {importRows.map((r,i) => (
                        <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                          <td style={{ padding:'8px 12px', fontFamily:'monospace', color:'var(--blue)', fontWeight:700, fontSize:12 }}>{r.sku}</td>
                          <td style={{ padding:'8px 12px', fontSize:12 }}>{r.product_name}</td>
                          <td style={{ padding:'8px 12px', fontSize:12, color: r.location_id ? 'var(--tx2)' : 'var(--red)' }}>{r.location_name || 'No location'}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{r.qty_received}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12 }}>{Number(r.unit_cost).toFixed(2)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontSize:12, color:'var(--green)' }}>{Number(r.selling_price).toFixed(2)}</td>
                          <td style={{ padding:'8px 12px', textAlign:'right', fontFamily:'monospace', fontWeight:600, fontSize:12 }}>{Number(r.line_total).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-primary" onClick={handleImportSave} disabled={saving}>{saving ? 'Importing...' : `Import ${importRows.length} Items`}</button>
              </>
            )}
          </div>
        </div>
      )}

      {showLCForm && isDraft && hasItems && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Add Landed Cost</span><button className="btn btn-secondary btn-sm" onClick={() => setShowLCForm(false)}>Cancel</button></div>
          <div className="card-body">
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr style={{background:'var(--sur2)'}}>
                <th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Cost type</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Amount</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Paid from</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Allocation</th><th style={{padding:'6px 8px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Description</th><th></th>
              </tr></thead>
              <tbody>
                {lcRows.map((r:any, i:number) => (
                  <tr key={i}>
                    <td style={{padding:4,width:130}}><select className="form-control" value={r.cost_type} onChange={e => setLcRows(x => x.map((y:any,j:number) => j===i ? {...y, cost_type:e.target.value} : y))}><option>Transport</option><option>Customs</option><option>Handling</option><option>Insurance</option><option>Other</option></select></td>
                    <td style={{padding:4,width:110}}><input className="form-control mono" style={{textAlign:'right'}} type="number" placeholder="0.00" value={r.amount} onChange={e => setLcRows(x => x.map((y:any,j:number) => j===i ? {...y, amount:e.target.value} : y))} /></td>
                    <td style={{padding:4}}><select className="form-control" value={r.credit_account_id} onChange={e => setLcRows(x => x.map((y:any,j:number) => j===i ? {...y, credit_account_id:e.target.value} : y))}><option value="">Select account</option>{accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select></td>
                    <td style={{padding:4,width:130}}><select className="form-control" value={r.allocation_method} onChange={e => setLcRows(x => x.map((y:any,j:number) => j===i ? {...y, allocation_method:e.target.value} : y))}><option value="by_value">By value</option><option value="by_qty">By quantity</option></select></td>
                    <td style={{padding:4}}><input className="form-control" value={r.description} onChange={e => setLcRows(x => x.map((y:any,j:number) => j===i ? {...y, description:e.target.value} : y))} /></td>
                    <td style={{padding:4,width:36}}>{lcRows.length > 1 && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLcRows(x => x.filter((_:any,j:number) => j!==i))}>x</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLcRows(x => [...x, { cost_type:'Transport', amount:'', credit_account_id:'', allocation_method:'by_value', description:'' }])}>+ Add cost</button>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <span style={{fontSize:12,color:'var(--tx3)'}}>Total <strong style={{fontFamily:'monospace'}}>{lcRows.reduce((t:number,x:any) => t + Number(x.amount||0), 0).toFixed(2)}</strong></span>
                <button className="btn btn-primary" onClick={handleAddLC} disabled={saving}>{saving ? 'Saving...' : 'Save costs'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasItems && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>Items Received</span><span className="record-count">{grn.items?.length} items</span></div>
          {isDraft && (() => {
            const t = (grn.items || []).reduce((s:number,x:any) => s + Number(x.qty_received) * Number(x.landed_per_unit || 0), 0);
            if (t <= 0) return null;
            const sp = llSplits.reduce((s:number,x:any) => s + Number(x.amount || 0), 0);
            return (
              <div style={{padding:'8px 12px',fontSize:12,borderBottom:'1px solid var(--bdr)'}}>
                <div style={{marginBottom:6}}>Per-unit landed costs: <strong style={{fontFamily:'monospace'}}>{t.toFixed(2)}</strong> — paid from:</div>
                {llSplits.map((s:any, i:number) => (
                  <div key={i} style={{display:'flex',gap:6,marginBottom:4,maxWidth:560}}>
                    <select className="form-control" style={{flex:1}} value={s.account_id} onChange={e => setLlSplits(x => x.map((y:any,j:number) => j===i ? {...y, account_id:e.target.value} : y))}><option value="">Select account</option>{accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}</select>
                    {llSplits.length > 1 && <input className="form-control mono" style={{width:110,textAlign:'right'}} placeholder="Amount" value={s.amount} onChange={e => setLlSplits(x => x.map((y:any,j:number) => j===i ? {...y, amount:e.target.value} : y))} />}
                    {llSplits.length > 1 && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLlSplits(x => x.filter((_:any,j:number) => j!==i))}>x</button>}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLlSplits(x => [...x, { account_id:'', amount:'' }])}>+ Split account</button>
                {llSplits.length > 1 && <span style={{marginLeft:10,color: Math.abs(sp - t) > 0.005 ? 'var(--red)' : 'var(--tx3)'}}>Splits {sp.toFixed(2)} of {t.toFixed(2)}</span>}
              </div>
            );
          })()}
          <table>
            <thead><tr>
              <th>SKU</th><th>Product</th><th>Location</th>
              <th style={{textAlign:'right'}}>Qty</th>
              <th style={{textAlign:'right'}}>Unit Cost</th>
              <th style={{textAlign:'right'}}>Total</th>
            </tr></thead>
            <tbody>
              {grn.items?.map((item: any) => (
                <tr key={item.id}>
                  <td className="td-code">{item.sku}</td>
                  <td>{item.product_name}</td>
                  <td style={{color:'var(--tx3)'}}>{item.location_name}</td>
                  <td className="td-num">{item.qty_received}</td>
                  <td className="td-num">{Number(item.unit_cost).toFixed(4)}</td>
                  <td className="td-num td-green">{(item.qty_received * item.unit_cost).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      

      {grn.landed_costs?.length > 0 && (
        <div className="card">
          <div className="card-header"><span>Landed Costs</span><span className="record-count">{grn.landed_costs.length} costs</span></div>
          <table>
            <thead><tr>
              <th>Type</th><th>Description</th><th>Account</th><th>Allocation</th>
              <th style={{textAlign:'right'}}>Amount</th>
              {isDraft && <th></th>}
            </tr></thead>
            <tbody>
              {grn.landed_costs.map((lc: any) => (
                <tr key={lc.id}>
                  <td><span className="badge badge-amber">{lc.cost_type}</span></td>
                  <td style={{color:'var(--tx3)'}}>{lc.description || '—'}</td>
                  <td style={{color:'var(--tx3)'}}>{lc.account_code} {lc.account_name}</td>
                  <td><span className="badge badge-blue">{lc.allocation_method}</span></td>
                  <td className="td-num">{Number(lc.amount).toFixed(2)}</td>
                  {isDraft && (
                    <td>
                      <button onClick={() => handleDeleteLC(lc.id)} style={{background:'none',border:'none',color:'var(--red)',cursor:'pointer'}}>
                        <Trash2 size={13}/>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default GrnDetail;


